import { useEffect } from 'react';
import './legal-modal.css';

/**
 * Impressum / Datenschutz as an in-page overlay: the client pages are a
 * standalone microsite (no router), so legal texts open as a modal from
 * the footer instead of navigating away. Content is static German
 * boilerplate for Acolori Medienproduktion GmbH.
 */
export type LegalKind = 'impressum' | 'datenschutz';

/**
 * Which surface the modal is rendered on. The Impressum is identical
 * everywhere, but the Datenschutz text differs: the share pages are accessed
 * via a non-public link by clients, while the internal app is a login-based
 * team workspace (accounts, sessions, audit logging).
 */
export type LegalSurface = 'share' | 'app';

export default function LegalModal({ kind, onClose, surface = 'share' }: { kind: LegalKind; onClose: () => void; surface?: LegalSurface }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="legal-overlay" role="dialog" aria-modal="true" aria-label={kind === 'impressum' ? 'Impressum' : 'Datenschutzerklärung'} onClick={onClose}>
      <div className="legal-panel" onClick={e => e.stopPropagation()}>
        <div className="legal-bar">
          <h2 className="legal-title">{kind === 'impressum' ? 'Impressum' : 'Datenschutzerklärung'}</h2>
          <button type="button" className="btn ghost legal-close" onClick={onClose}>Schließen ✕</button>
        </div>
        <div className="legal-body">
          {kind === 'impressum' ? <Impressum /> : <Datenschutz surface={surface} />}
        </div>
      </div>
    </div>
  );
}

function Impressum() {
  return (
    <>
      <h3>Angaben gemäß § 5 TMG</h3>
      <p>
        Acolori Medienproduktion GmbH<br />
        Riemenschneiderstieg 15<br />
        22607 Hamburg<br />
        Deutschland
      </p>
      <h3>Vertreten durch</h3>
      <p>Ben Liese</p>
      <h3>Kontakt</h3>
      <p>
        Telefon: 0162 6226262<br />
        E-Mail: hallo@acolori.de
      </p>
      <h3>Registereintrag</h3>
      <p>
        Handelsregister: Amtsgericht Hamburg<br />
        Registernummer: HRB 102195
      </p>
      <h3>Umsatzsteuer-ID</h3>
      <p>Umsatzsteuer-Identifikationsnummer gemäß § 27 a UStG: DE 814886653</p>
      <h3>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h3>
      <p>Ben Liese, Riemenschneiderstieg 15, 22607 Hamburg</p>
    </>
  );
}

function Datenschutz({ surface }: { surface: LegalSurface }) {
  const isApp = surface === 'app';
  return (
    <>
      <h3>1. Verantwortlicher</h3>
      <p>
        Verantwortlich für die Datenverarbeitung auf dieser Plattform ist die
        Acolori Medienproduktion GmbH, Riemenschneiderstieg 15, 22607 Hamburg,
        E-Mail: hallo@acolori.de.
      </p>
      {isApp ? (
        <>
          <h3>2. Zweck dieser Anwendung</h3>
          <p>
            Diese Anwendung ist der interne Arbeitsbereich der Acolori
            Medienproduktion GmbH zur Verwaltung von Projekten,
            Projektinhalten und Kundenkommunikation. Der Zugriff erfolgt
            ausschließlich über persönliche, passwort- bzw.
            passkey-geschützte Benutzerkonten für Teammitglieder und
            berechtigte Nutzer.
          </p>
          <h3>3. Verarbeitete Daten</h3>
          <p>
            Bei der Nutzung der Anwendung werden technisch notwendige Daten
            verarbeitet (IP-Adresse, Zeitpunkt des Zugriffs,
            Browser-Informationen), um die Anwendung sicher bereitzustellen
            und Missbrauch zu verhindern (Art. 6 Abs. 1 lit. f DSGVO). Zur
            Verwaltung der Benutzerkonten werden Anmeldedaten (Benutzername,
            Passwort in verschlüsselter Form bzw. Passkey-Daten) sowie
            Anmeldezeitpunkte verarbeitet (Art. 6 Abs. 1 lit. b DSGVO).
            Sicherheitsrelevante Aktionen (z.&nbsp;B. Anmeldungen,
            Statusänderungen, Freigaben) werden zudem protokolliert, um die
            Nachvollziehbarkeit und Sicherheit des Systems zu gewährleisten
            (Art. 6 Abs. 1 lit. f DSGVO). Im Rahmen der Projektarbeit
            eingegebene Inhalte (Nachrichten, Dateien, Notizen) werden
            gespeichert, um die Projektbearbeitung und -kommunikation zu
            ermöglichen (Art. 6 Abs. 1 lit. b DSGVO).
          </p>
          <h3>4. Cookies und Speicherung im Browser</h3>
          <p>
            Diese Anwendung verwendet keine Tracking- oder Marketing-Cookies.
            Es werden ausschließlich technisch notwendige Cookies und
            Informationen im Browser gespeichert (insbesondere zur
            Anmeldesitzung und zum Schutz vor unbefugten Zugriffen).
          </p>
        </>
      ) : (
        <>
          <h3>2. Zweck dieser Seite</h3>
          <p>
            Diese Seite dient der Bereitstellung von Projektinhalten (Vorschauen,
            Dateien, Projektinformationen) für Kunden und von Kunden benannte
            Empfänger. Der Zugriff erfolgt über einen individuellen,
            nicht öffentlichen Link.
          </p>
          <h3>3. Verarbeitete Daten</h3>
          <p>
            Beim Aufruf dieser Seite werden technisch notwendige Daten verarbeitet
            (IP-Adresse, Zeitpunkt des Zugriffs, Browser-Informationen), um die
            Seite sicher bereitzustellen und Missbrauch zu verhindern
            (Art. 6 Abs. 1 lit. f DSGVO). Sofern die Chat-Funktion genutzt wird,
            werden die eingegebenen Nachrichten und ggf. hochgeladene Dateien
            gespeichert, um die Projektkommunikation zu ermöglichen
            (Art. 6 Abs. 1 lit. b DSGVO).
          </p>
          <h3>4. Cookies und Speicherung im Browser</h3>
          <p>
            Diese Seite verwendet keine Tracking- oder Marketing-Cookies. Es werden
            ausschließlich technisch notwendige Informationen (z.&nbsp;B. eine
            Zugriffssitzung) im Browser gespeichert.
          </p>
        </>
      )}
      <h3>5. Eingesetzte Dienstleister (Auftragsverarbeiter) und Drittlandtransfers</h3>
      <p>
        Zur Bereitstellung dieser Plattform setzen wir folgende Dienstleister
        ein, mit denen Auftragsverarbeitungsverträge gemäß Art. 28 DSGVO
        bestehen. Soweit dabei Daten in die USA übertragen werden, erfolgt
        dies vorrangig auf Grundlage des Angemessenheitsbeschlusses der
        EU-Kommission zum EU-US Data Privacy Framework (DPF, Art. 45 DSGVO),
        sofern der jeweilige Anbieter dort zertifiziert ist; ergänzend bzw.
        soweit keine Zertifizierung besteht, auf Grundlage von
        Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO) mit
        zusätzlichen technischen Schutzmaßnahmen (insbesondere
        Transportverschlüsselung und Verschlüsselung gespeicherter Daten):
      </p>
      <ul>
        <li>
          <strong>Replit, Inc.</strong> (San Francisco, USA) – Hosting und
          Betrieb der Webanwendung.
        </li>
        <li>
          <strong>Cloudflare, Inc.</strong> (San Francisco, USA) – Speicherung
          von Projektdateien und Medien über Cloudflare R2 (Object Storage).
          Cloudflare ist unter dem EU-US Data Privacy Framework zertifiziert.
        </li>
        <li>
          <strong>Dropbox, Inc.</strong> (San Francisco, USA) – optionale
          Einbindung von Projektdateien aus einem verknüpften Dropbox-Konto
          des Kunden. Dropbox ist unter dem EU-US Data Privacy Framework
          zertifiziert.
        </li>
        <li>
          <strong>OpenAI, L.L.C.</strong> (San Francisco, USA) – KI-gestützte
          Zusammenfassungen und automatisierte Auswertungen im Rahmen der
          Projektbearbeitung. OpenAI ist unter dem EU-US Data Privacy
          Framework zertifiziert. Siehe hierzu im Einzelnen Abschnitt 6.
        </li>
        <li>
          <strong>LiveKit, Inc.</strong> (San Francisco, USA) – Echtzeit-
          Videokommunikation (Video-Calls) innerhalb der Plattform. Siehe
          hierzu im Einzelnen Abschnitt 7.
        </li>
      </ul>
      <h3>6. KI-gestützte Verarbeitung (OpenAI)</h3>
      <p>
        Für Zusammenfassungen und automatisierte Auswertungen von
        Projektinhalten nutzen wir ausschließlich die
        OpenAI&nbsp;API (Business-Schnittstelle), nicht die
        Endkunden-Produkte (z.&nbsp;B. ChatGPT). Nach den API-Bedingungen von
        OpenAI werden über die API übermittelte Inhalte nicht zum Training
        der KI-Modelle verwendet. Wir übermitteln dabei nur die für den
        jeweiligen Zweck erforderlichen Inhalte. Rechtsgrundlage ist die
        Erfüllung des Projektvertrags (Art. 6 Abs. 1 lit. b DSGVO) sowie
        unser berechtigtes Interesse an einer effizienten Projektbearbeitung
        (Art. 6 Abs. 1 lit. f DSGVO). Es findet keine automatisierte
        Entscheidungsfindung mit rechtlicher Wirkung im Sinne von
        Art. 22 DSGVO statt.
      </p>
      <h3>7. Videokommunikation (LiveKit)</h3>
      <p>
        Video- und Audiodaten der Video-Calls werden grundsätzlich nur
        flüchtig und transportverschlüsselt übertragen und nicht dauerhaft
        gespeichert. Eine Aufzeichnung oder automatische Transkription eines
        Gesprächs erfolgt nur, wenn dies den Teilnehmenden vor Beginn
        mitgeteilt wird; Rechtsgrundlage ist in diesem Fall die Einwilligung
        der Teilnehmenden (Art. 6 Abs. 1 lit. a DSGVO). Die Teilnahme an
        Video-Calls ist freiwillig.
      </p>
      <h3>8. Speicherdauer</h3>
      <p>
        Server-Logfiles mit IP-Adressen, die zur Missbrauchsverhinderung und
        Fehleranalyse erhoben werden, werden in der Regel nach spätestens
        14 Tagen gelöscht bzw. anonymisiert, soweit kein sicherheitsrelevanter
        Vorfall eine längere Aufbewahrung erfordert.
      </p>
      {isApp ? (
        <p>
          Projektinhalte werden für die Dauer der Projektbearbeitung
          gespeichert und nach Projektabschluss gelöscht, soweit keine
          gesetzlichen Aufbewahrungspflichten (z.&nbsp;B. handels- und
          steuerrechtliche Fristen) bestehen. Benutzerkonten und zugehörige
          Protokolldaten werden gespeichert, solange das Konto besteht bzw.
          soweit dies für die Sicherheit des Systems erforderlich ist.
        </p>
      ) : (
        <p>
          Zugriffslinks sind zeitlich befristet. Die über einen Link
          bereitgestellten Inhalte werden nach Ablauf des Links bzw. nach
          Projektabschluss gelöscht, in der Regel spätestens 90 Tage nach
          Ablauf des Zugriffslinks, soweit keine gesetzlichen
          Aufbewahrungspflichten bestehen.
        </p>
      )}
      <h3>9. Ihre Rechte</h3>
      <p>
        Sie haben das Recht auf Auskunft, Berichtigung, Löschung,
        Einschränkung der Verarbeitung, Datenübertragbarkeit sowie Widerspruch
        gegen die Verarbeitung Ihrer personenbezogenen Daten. Zudem besteht
        ein Beschwerderecht bei einer Datenschutz-Aufsichtsbehörde. Wenden Sie
        sich hierzu an: hallo@acolori.de.
      </p>
    </>
  );
}
